import React, { useEffect } from 'react';
import { View, Modal, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';

import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { trackAuthEvent } from '../../services/analytics';
import { brand } from '../../design-system/theme/tokens';

const PAYWALL_TRIGGER = 'chat_limit';

const COLORS = {
  primary: brand.primary,
  primaryLight: '#3B82F6',
  success: '#10B981',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#9CA3AF' },
};

interface TrialChatLimitModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export default function TrialChatLimitModal({ visible, onDismiss }: TrialChatLimitModalProps) {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    if (visible) {
      trackAuthEvent('trial_paywall_viewed', { trigger: PAYWALL_TRIGGER });
    }
  }, [visible]);

  const handleSubscribe = () => {
    trackAuthEvent('trial_paywall_cta_clicked', { trigger: PAYWALL_TRIGGER, destination: 'gated_plan_selection' });
    onDismiss();
    router.push(ROUTES.GATED_PLAN_SELECTION);
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <View className="mx-6 rounded-2xl bg-white overflow-hidden w-full max-w-sm" style={{ maxWidth: 380 }}>
          {/* Header strip */}
          <LinearGradient
            colors={[COLORS.primary, COLORS.primaryLight]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ paddingVertical: 14, paddingHorizontal: 20, alignItems: 'center' }}
          >
            <Text className="text-white text-sm font-semibold">
              {t('trial.chatLimit.badge') || 'Free Trial'}
            </Text>
          </LinearGradient>

          <View className="p-6">
            <View className="items-center mb-5">
              {/* Message dots visualization */}
              <View className="flex-row items-center mb-4" style={{ gap: 6 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <View
                    key={i}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: COLORS.primary,
                    }}
                  />
                ))}
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    borderWidth: 1.5,
                    borderColor: COLORS.text.muted,
                    borderStyle: 'dashed',
                  }}
                />
              </View>

              <Text className="text-xl font-bold text-center mb-2" style={{ color: COLORS.text.primary }}>
                {t('trial.chatLimit.title') || "You've used your trial messages"}
              </Text>

              <Text className="text-sm text-center leading-5" style={{ color: COLORS.text.secondary }}>
                {t('trial.chatLimit.description') ||
                  'Unlock unlimited conversations to keep improving your coaching skills with personalized AI feedback.'}
              </Text>
            </View>

            {/* What subscribers get */}
            <View className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#F8FAFC' }}>
              <Text className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: COLORS.text.muted }}>
                {t('trial.chatLimit.withPro') || 'With a subscription'}
              </Text>
              {[
                { icon: 'all-inclusive' as const, text: t('trial.chatLimit.feature1') || 'Unlimited coaching conversations' },
                { icon: 'upload-file' as const, text: t('trial.chatLimit.feature2') || 'Unlimited transcript analyses' },
                { icon: 'trending-up' as const, text: t('trial.chatLimit.feature3') || 'Track progress over time' },
              ].map((item, idx) => (
                <View key={idx} className="flex-row items-center mb-2">
                  <View
                    className="w-6 h-6 rounded-full items-center justify-center mr-2"
                    style={{ backgroundColor: `${COLORS.success}15` }}
                  >
                    <MaterialIcons name={item.icon} size={14} color={COLORS.success} />
                  </View>
                  <Text className="text-sm flex-1" style={{ color: COLORS.text.secondary }}>{item.text}</Text>
                </View>
              ))}
            </View>

            {/* Launch offer */}
            <View className="mb-3 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', borderWidth: 1, borderColor: '#FCD34D' }}>
              <Text className="text-xs font-semibold text-center" style={{ color: '#92400E' }}>
                {`🎉 ${t('trial.launchOffer') || 'Launch offer: 20% off your first 3 months — use code LAUNCH20 at checkout'}`}
              </Text>
            </View>

            {/* CTA */}
            <TouchableOpacity onPress={handleSubscribe} className="w-full rounded-xl overflow-hidden mb-1" activeOpacity={0.85}>
              <LinearGradient
                colors={[COLORS.primary, '#2563EB']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ paddingVertical: 14, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
              >
                <MaterialIcons name="rocket-launch" size={18} color="white" />
                <Text className="text-white font-bold ml-2 text-base">
                  {t('trial.chatLimit.subscribeCTA') || 'Continue Coaching'}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text className="text-xs text-center mt-1 mb-2" style={{ color: COLORS.text.muted }}>
              {t('trial.chatLimit.guarantee') || 'Cancel anytime. Instant access.'}
            </Text>

            <TouchableOpacity onPress={onDismiss} className="py-2">
              <Text className="text-center text-sm" style={{ color: COLORS.text.muted }}>
                {t('trial.chatLimit.dismiss') || 'Not now'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}
